// Phase 1C — GridViewer top-level component. Wires camera (ref, not state),
// canvas, four fixed tiles, stats poll, detail panel. Read-only, no wallet.
//
// SEP 5.b.2 additions:
//   - Explicit mode: "browse" (pan/zoom/tap) vs "select" (drag rectangle)
//   - Uncontrolled by default; parent may control via `mode`/`onModeChange`
//   - `onCommitSelection` fires on pointerup with the canonical GridSelection
//   - Selection overlay is rendered in the canvas via renderer.ts
//   - Wheel zoom keeps working in BOTH modes (spec §V.20)
//   - Escape cancels an active draft (§L)
//   - `layout="fill"` embeds the viewer inside a flex parent (purchase route)
//
// Reviewer P0/P1 fixes wired here:
//   - real pointer events (mouse+touch+pen) via ./gesture state machine
//   - wheel via native addEventListener with { passive:false }
//   - tile publication via ./tile-reducer (applyPublication) — the SAME
//     production reducer tests exercise
//   - refresh scheduling debounced (150ms) and confined to visible tiles
//   - stats-driven staleness triggers a real refresh, not just a state flag
//   - snapshots survive abort / HTTP error
//   - one-shot skew retry gated by ./skew-coordinator
//   - cell + parcel-detail races protected by seq guards; cross-selection
//     invalidates the OTHER outstanding kind
//   - same-origin API via ./api (no VITE_API_URL)
//   - centralized chain validation via ./chain (ChainMismatchError)
//   - mobile bottom-sheet layout via matchMedia
//   - "Outside grid" state — no /cell call when world coord outside [0,1000)
//   - hover tooltip only for fine pointers
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { makeApi, ViewerApiError, type ViewerApi } from "./api";
import {
  buildSpatialHash,
  clampCamera,
  hitTest,
  screenToWorld,
  worldToScreen,
  zoomAt,
  MAX_ZOOM,
  type Camera,
} from "./camera";
import { ChainMismatchError } from "./chain";
import {
  initGesture,
  onPointerCancel,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  type GestureState,
} from "./gesture";
import { deriveMapStatus } from "./map-status";
import {
  beginDraft,
  cancelDraft,
  clearCommitted,
  commitDraft,
  deriveDraftPreview,
  initRectSelection,
  isInsideGrid,
  setMode as setRectMode,
  updateDraft,
  type RectSelectionState,
  type SelectionMode,
} from "./rect-selection";
import { decideTouchOwner, ownerSuppressesNativeScroll } from "./touch-ownership";
import { RefreshCycle } from "./refresh-cycle";
import { render, resizeCanvasForDpr, setRepaintHook, type BrandImageLayerItem } from "./renderer";
import { useBrandImageDiscovery } from "./useBrandImageDiscovery";
import { usePublicationDiscovery } from "./usePublicationDiscovery";
import { GridPublicationLayer } from "./GridPublicationLayer";
import { ContentPointerContext, type ContentPointerArbiter } from "./contentPointer";
import { GridArtworkLayer, ARTWORK_SELECT_EVENT } from "./GridArtworkLayer";
import { useArtworkPlacementDiscovery } from "./useArtworkPlacementDiscovery";
import { isDetailBand, isIntermediateBand } from "../domain/lod";
import { useSnapshotVersion } from "./useSnapshotVersion";
import { GridIntermediateLayer, type IntermediateTerritory } from "./GridIntermediateLayer";
import { Enter3DAffordance, type Enter3DItem } from "../ui/Enter3DAffordance";
import { Public3DOverlay, type Public3DTarget } from "../ui/Public3DOverlay";
import { enter3dEligible } from "../domain/voxel-lod";
import { Grid3DFootprintLayer, type Voxel3DFootprintItem } from "./Grid3DFootprintLayer";
import { unionBoundsWorld } from "./brand-image-geometry";
import { topDownProjection, sceneBoundsXY } from "../domain/voxel3d";
import { refreshSignalChanged } from "../lib/purchase-refresh";
import { SelectionCoordinator } from "./selection-coordinator";
import { SkewRetryBudget } from "./skew-coordinator";
import {
  initStatsState,
  nextStatsState,
  type StatsUiState as StatsReducerState,
} from "./stats-state";
import { applyPublication, beginRefresh } from "./tile-reducer";
import { visibleTiles, isInsideWorld } from "./tile-visibility";
import { attachWheelRouting } from "./wheel-router";
import {
  allTileCoords,
  emptyTile,
  fetchTileFull,
  markStaleAgainstStats,
  snapshotRange,
  unionParcels,
} from "./tiles";
import type { GridSelection } from "../lib/grid-selection";
import type { Parcel, TileRecord } from "./types";
import {
  contextStatesEqual,
  deriveViewerContextState,
  type ViewerCellState,
  type ViewerContextState,
  type ViewerParcelDetail,
} from "./context-state";
import { useTerritoryName } from "../ui/useTerritoryName";

const STATS_POLL_MS = 15_000;
const REFRESH_DEBOUNCE_MS = 150;

type StatsUiState = StatsReducerState;

// H2.3 WO#3a — local aliases pointing to the CANONICAL types shared
// with the shell. `DetailData` and `CellUi` remain in scope for
// backwards-compatible readability of the existing viewer code paths.
type DetailData = ViewerParcelDetail;
type CellUi = ViewerCellState | null;

/** Pure helper for the routing smoke test in App.tsx. */
export { routeFrom } from "./route";


// WO-VRD-21 (Claudio) — default & recenter camera: the 1000×1000 world is
// centered in the viewport at a FIXED zoom of 0.50× (both on first open and
// when the "recenter grid" button is pressed). Presentation-only helper; it
// does NOT modify the locked camera math in camera.ts.
function fitAtScale(
  viewportW: number,
  viewportH: number,
  scale = 0.45,
  worldSize = 1000,
): Camera {
  const worldPixels = worldSize * scale;
  return {
    x: -((viewportW - worldPixels) / 2) / scale,
    y: -((viewportH - worldPixels) / 2) / scale,
    scale,
  };
}

// MACRO #31 PAGE 3 — initial / fit zoom level.
// Desktop (>=768px) keeps the canonical 0.45. On mobile, 0.45 renders the
// 1000-cell world WIDER than the screen (it feels "giant" and clipped), so
// we fit the whole square world into the smaller viewport dimension with a
// comfortable margin, capped at 0.45 so it never zooms IN beyond desktop.
function initialScale(viewportW: number, viewportH: number): number {
  if (viewportW >= 768) return 0.45;
  const fit = (Math.min(viewportW, viewportH) * 0.7) / 1000;
  return Math.max(0.12, Math.min(0.45, fit));
}

export interface GridViewerProps {
  api?: ViewerApi;
  /** Controlled selection mode. If omitted, mode is uncontrolled ("browse"). */
  mode?: SelectionMode;
  /** Change handler for the toggle button (controlled or uncontrolled). */
  onModeChange?: (m: SelectionMode) => void;
  /** Fires on pointerup that commits a rectangle selection. */
  onCommitSelection?: (sel: GridSelection) => void;
  /** WO-VRD-31 (Claudio) — fired when the user clicks/starts a selection
   * OUTSIDE the grid in select mode; the shell clears the committed
   * rectangle so the Context drawer dismisses (mirrors Inspect void-tap). */
  onClearSelection?: () => void;
  /** Layout: "viewport" (default, height: 100vh) or "fill" (height: 100%). */
  layout?: "viewport" | "fill";
  /**
   * H2.3 WO#2 §7 — visual chrome mode.
   *   - "internal" (default, backwards-compatible): renders the legacy
   *     Phase 2A header (THE GRID · stats · Select · Fit Grid) inside
   *     the viewer wrapper. Existing tests + standalone routes keep it.
   *   - "external": the shell (GridWorkspace) owns the visible chrome.
   *     The internal header is hidden. Renderer / camera / gesture /
   *     tiles / selection semantics are UNCHANGED — only the DOM
   *     header block is suppressed.
   */
  chrome?: "internal" | "external";
  /**
   * H2.3 WO#2 §9 — HUD live pointer coordinate callback. Fires with the
   * current world-space {x,y} while the pointer is over the viewer, or
   * `null` when the pointer leaves or lands outside the 1000x1000 grid.
   * The value is derived from the SAME internal `hoverPos` state that
   * powers the legacy tooltip — no new pointer subscription is added.
   */
  onHoverChange?: (world: { x: number; y: number } | null) => void;
  /**
   * H2.3 WO#2 §9 — HUD live zoom callback. Fires whenever the camera
   * scale changes (wheel, pinch, fit, imperative zoom). Value is
   * `cameraRef.current.scale` (canonical Phase 2A camera math).
   */
  onCameraChange?: (state: { scale: number }) => void;
  /**
   * H2.3 WO#3a §Context — Canonical context-state callback (declarative
   * push channel). Fires whenever the viewer-owned browse-mode context
   * transitions between `empty | selection | parcel`. This is the ONLY
   * reading channel for the shell — no imperative getter exists on
   * `GridViewerHandle` on purpose (single source of truth, no shadow
   * state). Purchase composition is a shell concern (see
   * `ui/grid-context.ts`); the viewer does NOT emit `purchase`.
   */
  onContextStateChange?: (state: ViewerContextState) => void;
  /**
   * WO#4a §15 — canonical zone geometry overlay. When non-null the
   * renderer paints the resolved-disjoint region set BEFORE parcels
   * and BELOW the selection/hover overlays. The viewer does NOT
   * verify the manifest — the shell owns identity/authority safety
   * (see `SaleAuthorityProvider` + `useOverlayRenderable`).
   */
  zoneOverlay?: import("../lib/zone-manifest").ZoneManifest | null;
  /**
   * WO-VRD-33 — "Reveal Areas" presentation toggle (forwarded to the
   * renderer). Highlights the canonical zone taxonomy in fluorescence
   * while active. Presentation-only; no engine involvement.
   */
  revealZones?: boolean;
  /** WO-VRD-33 — hovered legend category (isolates one zone). */
  revealHoverRole?: import("../lib/zone-manifest").ZoneRole | null;
  /**
   * #15 Show & Drag — keeps placed Frozen Artwork MOUNTED and grabbable even
   * at overview zoom (bypasses the detail-LOD gate while active) so a piece
   * can be dragged between distant parcels from a panoramic view.
   */
  artworkOverlay?: boolean;
  /**
   * WO-VRD-34 — live selection readout. Emits the CURRENT rectangle
   * selection (draft while dragging, committed after release, or `null`
   * when none) so the shell can show how many slots are being selected
   * next to the Select toggle. Presentation-only; derived from the same
   * canonical `selectionOverlay` the renderer already paints.
   */
  onSelectionChange?: (sel: GridSelection | null) => void;
  /**
   * Macro #21 §5 — canonical occupancy readout. Emits the CURRENT set of
   * canonical (indexer-sourced) parcel footprints already loaded in the
   * viewer tiles whenever it changes. Consumed by the purchase shell for a
   * FAIL-FAST selection guard (block a draw that overlaps existing land
   * BEFORE the wallet). This is NOT a new authority and performs NO fetch —
   * it forwards the exact parcels the renderer already paints. Absence of
   * overlap NEVER implies availability (indexer may lag); the chain remains
   * the final authority.
   */
  onOccupancyChange?: (parcels: import("../lib/selection-occupancy").OccupancyRect[]) => void;
  /**
   * HOTFIX purchase-indexed-grid-refresh — a monotonically-bumped nonce. When
   * it CHANGES to a new value (never on its initial mount value) the viewer
   * runs the existing authoritative refresh cycle (reloads parcel tiles from
   * the backend). Used by the shell to reveal a newly indexed purchased parcel
   * without a manual browser refresh. Renderer / camera / gesture / tile
   * semantics are otherwise untouched.
   */
  refreshSignal?: number;
  /**
   * WO-VRD-9 — canonical territory sync status for the shell top toolbar.
   * Emits "live" | "cached" | "chain" whenever the derived state changes.
   * The in-canvas badge is then rendered by the shell next to the
   * "TERRITORY" header on desktop; the viewer keeps its own badge only
   * on mobile (where the top toolbar is not shown).
   */
  onStatusChange?: (level: "live" | "cached" | "chain") => void;
  /**
   * Fires ONCE when the first parcel refresh cycle settles (the initial
   * tiles have loaded + painted). Lets the shell gate a loading screen so
   * parcels + zone overlay reveal together instead of staggering in.
   */
  onFirstReady?: () => void;
  /**
   * Macro #20 (Admin Clients) — declarative camera focus target. When set to a
   * finite {fx,fy,span}, the viewer frames that world point (same math as the
   * "Locate on Grid" deep-link, via canonical `clampCamera`). Used to embed the
   * REAL grid inside the admin Clients panel focused on a client's parcels
   * WITHOUT touching the global route hash. `ov` relaxes the zoom floor so a
   * wide footprint pulls into a panoramic frame. Re-applies whenever the object
   * identity changes (memoize upstream).
   */
  focus?: { fx: number; fy: number; span: number; ov?: boolean } | null;
}

/**
 * H2.3 WO#2 §8 — Clean viewer control API.
 * Exposed via forwardRef + useImperativeHandle. Callers (GridWorkspace,
 * GridControls) drive the viewer through this typed surface INSTEAD of
 * synthesizing DOM events. Renderer / camera math / gesture SM are
 * untouched — each method delegates to canonical helpers (fitGrid,
 * zoomAt) around the SAME cameraRef the pointer/gesture pipeline uses.
 */
export interface GridViewerHandle {
  /** Zoom in around the current viewport center. */
  zoomIn: () => void;
  /** Zoom out around the current viewport center. */
  zoomOut: () => void;
  /** Fit the full 1000x1000 grid into the current viewport. */
  fitGrid: () => void;
  /** Read the current camera scale (canonical Phase 2A units). */
  getScale: () => number;
}

export default forwardRef<GridViewerHandle, GridViewerProps>(function GridViewer(
  {
    api: apiOverride,
    mode: modeProp,
    onModeChange,
    onCommitSelection,
    onClearSelection,
    layout = "viewport",
    chrome = "internal",
    onHoverChange,
    onCameraChange,
    onContextStateChange,
    zoneOverlay,
    revealZones,
    revealHoverRole,
    artworkOverlay,
    onSelectionChange,
    onOccupancyChange,
    onStatusChange,
    onFirstReady,
    refreshSignal,
    focus,
  }: GridViewerProps = {},
  ref,
) {
  const api = useMemo(() => apiOverride ?? makeApi(), [apiOverride]);
  const onFirstReadyRef = useRef(onFirstReady);
  onFirstReadyRef.current = onFirstReady;
  const firstReadyFiredRef = useRef(false);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const cameraRef = useRef<Camera>({ x: 0, y: 0, scale: 1 });
  const dprRef = useRef<number>(
    typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1,
  );
  const dirtyRef = useRef<boolean>(true);
  // WO-VRD-33 — mirror of the Reveal-Areas toggle read by the RAF loop.
  const revealZonesRef = useRef<boolean>(!!revealZones);
  const revealHoverRoleRef = useRef(revealHoverRole ?? null);
  // #15 Show & Drag — mirror of the artwork-overlay toggle read by the RAF loop.
  const artworkOverlayRef = useRef<boolean>(!!artworkOverlay);
  // WO#5 §CAMERA-PERSISTENCE — one-shot flag that gates the initial
  // `fitGrid` inside the render-loop effect. Prior to WO#5 the effect
  // depended on `[allParcels, hoveredId, selectedId, degraded,
  // selectionOverlay]`; every data-polling tick mutated `allParcels`
  // → the effect re-ran → `initFit()` clobbered the user's pan/zoom.
  // The camera itself is a ref (survives renders); the ONLY reason
  // it was resetting was this unguarded initFit on re-run. Guarding
  // it here restores camera authority across every refresh cycle.
  const didInitFitRef = useRef<boolean>(false);
  // WO#5 §CAMERA-RESIZE — the camera should refit on genuine viewport
  // resizes (device rotation, drawer close, layout shift after fonts
  // load, initial layout stabilisation) as long as the USER has not
  // taken authority of the camera. Once the user pans / zooms / taps
  // a parcel, they own the camera and resizes MUST NOT clobber it.
  // The Fit-Grid button clears this flag → the user explicitly asks
  // the camera to become resize-adaptive again.
  const userAdjustedCameraRef = useRef<boolean>(false);
  // Track the wrap size we last fit against so ResizeObserver can
  // ignore no-op observer callbacks (React StrictMode / hidden nodes).
  const lastFitSizeRef = useRef<{ w: number; h: number } | null>(null);
  const rafRef = useRef<number | null>(null);
  // PERF — quality gating: the camera of the last PAINTED frame + a trailing
  // timer. While the camera keeps changing (pan/zoom/inertia/tween) frames are
  // "fast" (no decorative shadowBlur); a short debounce after motion settles
  // marks one more frame dirty so it repaints at "full" quality.
  const lastPaintedCamRef = useRef<{ x: number; y: number; scale: number } | null>(null);
  const qualityTimerRef = useRef<number | null>(null);
  // WO-CONTEST §BAND-FADE (Claudio) — animated intensity [0..1] for the 3
  // concentric perimeter bands. Driven by the POINTER/CLICK state
  // (`panActiveRef`), NOT camera motion: eased toward 0 while a pan drag is
  // held (button down), and back toward 1 the instant the button is released
  // (~350ms medium fade, symmetric). Holding still mid-drag keeps them off;
  // post-release inertia never delays the fade-in. `Ts` tracks the previous
  // RAF-tick timestamp for frame-rate-independent easing.
  const bandGlowRef = useRef<number>(1);
  const bandGlowTsRef = useRef<number | null>(null);
  // True while a pan drag is actively HELD (pointer down + panning). Set on the
  // first pan move, cleared on pointer up / cancel. Sole driver of the band fade.
  const panActiveRef = useRef<boolean>(false);
  // WO-VRD-11 §SNAP-FIT — RAF handle for the smooth "center/fit" camera
  // tween. Presentation-only: it interpolates between two already-valid
  // camera states; no camera math is changed.
  const fitTweenRef = useRef<number | null>(null);
  // WO-VRD-11 §SNAP-FIT — cancel any in-flight fit tween (called on new
  // fit, or when the user takes camera authority via pan / zoom / wheel).
  const cancelFitTween = useCallback(() => {
    if (fitTweenRef.current != null) {
      cancelAnimationFrame(fitTweenRef.current);
      fitTweenRef.current = null;
    }
  }, []);
  const gestureRef = useRef<GestureState>(initGesture());
  const abortsRef = useRef<Map<string, AbortController>>(new Map());
  const selectionRef = useRef<SelectionCoordinator>(new SelectionCoordinator());
  const cycleRef = useRef<RefreshCycle>(new RefreshCycle());
  const skewBudgetRef = useRef<SkewRetryBudget>(new SkewRetryBudget(0));
  const refreshTimerRef = useRef<number | null>(null);

  const [tiles, setTiles] = useState<Record<string, TileRecord>>(() => {
    const t: Record<string, TileRecord> = {};
    for (const c of allTileCoords()) {
      t[`${c.tileX}:${c.tileY}`] = emptyTile(c.tileX, c.tileY);
    }
    return t;
  });
  const [statsUi, setStatsUi] = useState<StatsUiState>(() => initStatsState());
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  // WO#5 §HUD — split the hover state into TWO orthogonal channels:
  //   - `hoverPos` = SCREEN-space pointer position (px inside the wrap).
  //     Used ONLY by the HoverTip tooltip which is DOM-absolutely
  //     positioned in the wrap's coordinate space.
  //   - `hoverWorld` = WORLD-space pointer position, derived via
  //     `screenToWorld`. This is what the CoordinateHUD's X·Y readout
  //     ACTUALLY needs to show truthful territory coordinates.
  // Prior to WO#5 the same `hoverPos` (screen px) was blindly passed
  // through `onHoverChange` as if it were world → the HUD read screen
  // pixels. See `handlePointerMove` for the WO#5 corrected split.
  const [hoverPos, setHoverPos] = useState<{ x: number; y: number } | null>(null);
  // WO-VRD-34b — screen-space pointer position while drawing a selection,
  // used to float a small "w×h" label next to the selection crosshair.
  const [draftScreen, setDraftScreen] = useState<{ x: number; y: number } | null>(null);
  const [hoverWorld, setHoverWorld] = useState<{ x: number; y: number } | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<DetailData | null>(null);
  // PUBLICATION → GRID: committed camera published to React ONLY at the detail
  // LOD (null at coarse/overview LOD → overlay absent). Bumped by the RAF loop.
  const [pubCam, setPubCam] = useState<import("./camera").Camera | null>(null);
  const lastPubCamRef = useRef<import("./camera").Camera | null>(null);
  // #17 INTERMEDIATE band camera (published between the overview cutoff and the
  // detail floor → drives the lightweight content-presence layer). Mutually
  // exclusive with `pubCam`.
  const [interCam, setInterCam] = useState<import("./camera").Camera | null>(null);
  const lastInterCamRef = useRef<import("./camera").Camera | null>(null);
  const [cellUi, setCellUi] = useState<CellUi>(null);
  const [conflict, setConflict] = useState<Parcel[] | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== "undefined"
      ? window.matchMedia?.("(max-width: 720px)")?.matches ?? false
      : false,
  );
  const [isFinePointer, setIsFinePointer] = useState<boolean>(() =>
    typeof window !== "undefined"
      ? window.matchMedia?.("(pointer: fine)")?.matches ?? true
      : true,
  );

  // ---- SEP 5.b.2: rectangle selection state (mode + draft + committed) ----
  // Mode is CONTROLLED when `modeProp` is provided; otherwise uncontrolled.
  const [uncontrolledMode, setUncontrolledMode] =
    useState<SelectionMode>("browse");
  const mode: SelectionMode = modeProp ?? uncontrolledMode;
  const [rectSel, setRectSel] = useState<RectSelectionState>(() =>
    initRectSelection("browse"),
  );
  // Keep the state machine's `mode` in sync with the effective mode.
  useEffect(() => {
    setRectSel((prev) => {
      if (prev.mode === mode) return prev;
      const next = setRectMode(prev, mode);
      // WO-VRD-6 §SELECT-CLEAR — canonical `setRectMode` keeps `committed`
      // when leaving SELECT. Per Claudio the drawn rectangle must VANISH
      // from the Grid the moment we return to Inspect (browse), so we drop
      // the committed selection here too. Presentation/UX only — the
      // canonical rect-selection math is untouched.
      return mode === "browse" ? { ...next, committed: null } : next;
    });
    dirtyRef.current = true;
  }, [mode]);
  // WO#4a — mark dirty when the zone overlay identity changes so the
  // next frame renders the new manifest (or clears the overlay).
  useEffect(() => {
    dirtyRef.current = true;
  }, [zoneOverlay?.identity.manifest_hash]);
  // WO-VRD-33 — toggling Reveal Areas repaints; the RAF loop then keeps
  // repainting while active so the fluorescent pulse animates.
  useEffect(() => {
    revealZonesRef.current = !!revealZones;
    dirtyRef.current = true;
  }, [revealZones]);
  useEffect(() => {
    revealHoverRoleRef.current = revealHoverRole ?? null;
    dirtyRef.current = true;
  }, [revealHoverRole]);
  useEffect(() => {
    artworkOverlayRef.current = !!artworkOverlay;
    dirtyRef.current = true;
  }, [artworkOverlay]);

  // A ref mirror so pointer handlers can read the freshest rectSel.
  // Claudio — mobile page-scroll escape hatch. A single-finger touch that
  // STARTS outside the grid plane must scroll the page (not pan the Grid), so
  // the Territory section is never a touch dead-end. Tracked here; consumed in
  // the pointer handlers. Desktop (mouse) is unaffected.
  const outsideScrollRef = useRef<{ pointerId: number; lastY: number } | null>(null);
  // MACRO #31 HOTFIX — native-scroll touch ownership, frozen at the FIRST touch
  // of a sequence: "grid" (plane/pinch) suppresses native scroll, "page" lets
  // the browser scroll the narrative. Reset to null when all fingers lift.
  const touchSeqOwnerRef = useRef<"grid" | "page" | null>(null);
  const rectSelRef = useRef<RectSelectionState>(rectSel);
  useEffect(() => {
    rectSelRef.current = rectSel;
  }, [rectSel]);
  const handleToggleMode = useCallback(() => {
    const next: SelectionMode = mode === "browse" ? "select" : "browse";
    if (modeProp == null) setUncontrolledMode(next);
    onModeChange?.(next);
  }, [mode, modeProp, onModeChange]);

  // ---- SEP 5.b.2 Selection overlay for the renderer (must be defined
  //      BEFORE the render-loop effect that consumes it). ----
  const selectionOverlay = useMemo(() => {
    const preview = deriveDraftPreview(rectSel);
    if (preview) return { selection: preview, kind: "draft" as const };
    if (rectSel.committed)
      return { selection: rectSel.committed, kind: "committed" as const };
    return null;
  }, [rectSel]);

  // WO-VRD-34 — emit the live selection (draft preview or committed) so the
  // shell can render the slot count next to the Select toggle. `null` clears.
  useEffect(() => {
    onSelectionChange?.(selectionOverlay?.selection ?? null);
  }, [selectionOverlay, onSelectionChange]);

  const allParcels = useMemo(() => unionParcels(Object.values(tiles)), [tiles]);
  const spatial = useMemo(() => buildSpatialHash(allParcels, 32), [allParcels]);

  // Macro #21 §5 — emit the canonical occupancy footprint (indexer-sourced,
  // already loaded) to the shell for the fail-fast purchase guard. No fetch,
  // no new authority — a straight projection of the parcels already painted.
  useEffect(() => {
    onOccupancyChange?.(
      allParcels.map((p) => ({ x: p.x, y: p.y, w: p.w, h: p.h, owner: p.owner })),
    );
  }, [allParcels, onOccupancyChange]);

  // ---- LOD0 Brand Image presentation layer (discovery + textures) ----
  // Presentation ONLY: never consulted by hit-test / selection / gesture.
  const markDirty = useCallback(() => {
    dirtyRef.current = true;
  }, []);
  // COSMIC SKIN — let the renderer request one repaint once the nebula
  // environment raster has decoded (otherwise an idle grid would not
  // repaint to reveal it). Presentation only.
  useEffect(() => {
    setRepaintHook(markDirty);
    return () => setRepaintHook(null);
  }, [markDirty]);
  const { renderables: brandRenderables, textures: brandTextures } =
    useBrandImageDiscovery(allParcels, markDirty, true);
  const brandRenderablesRef = useRef(brandRenderables);
  // PUBLICATION → GRID discovery (bounded, enabled only at the detail LOD).
  // #17: the region visual-version poll drives precise, per-territory content
  // invalidation (publish / placement / transfer / brand → auto-refresh with no
  // manual reload, no global bust). Enabled only once content is discoverable
  // (intermediate or detail band); overview stays pure geography + brand image.
  const contentCam = interCam ?? pubCam;
  const snapshotViewport = useMemo(() => {
    const c = contentCam;
    if (!c) return null;
    const vw = typeof window !== "undefined" ? window.innerWidth : 1280;
    const vh = typeof window !== "undefined" ? window.innerHeight : 800;
    const GRID = 1000;
    const AREA = 250_000;
    const x0 = Math.max(0, Math.min(GRID, Math.floor(c.x)));
    const y0 = Math.max(0, Math.min(GRID, Math.floor(c.y)));
    if (x0 >= GRID || y0 >= GRID) return null;
    let w = Math.max(1, Math.min(GRID - x0, Math.ceil(vw / c.scale) + 2));
    let h = Math.max(1, Math.min(GRID - y0, Math.ceil(vh / c.scale) + 2));
    if (w * h > AREA) {
      h = Math.max(1, Math.floor(AREA / w));
      if (w * h > AREA) w = Math.max(1, Math.floor(AREA / h));
    }
    return { x: x0, y: y0, w, h };
  }, [contentCam]);
  const snapshotState = useSnapshotVersion({ viewport: snapshotViewport, enabled: contentCam != null });

  const parcelById = useMemo(() => {
    const m = new Map<number, (typeof allParcels)[number]>();
    for (const p of allParcels) m.set(p.id, p);
    return m;
  }, [allParcels]);
  const intermediateItems = useMemo<IntermediateTerritory[]>(() => {
    if (interCam == null) return [];
    const rows = snapshotState.snapshot?.territories ?? [];
    const out: IntermediateTerritory[] = [];
    for (const r of rows) {
      const units: IntermediateTerritory["units"] = [];
      let owner = "";
      for (const pid of r.member_parcel_ids) {
        const p = parcelById.get(pid);
        if (p) {
          units.push({ id: p.id, x: p.x, y: p.y, w: p.w, h: p.h, owner: p.owner });
          owner = p.owner;
        }
      }
      if (units.length === 0) continue;
      out.push({
        identityId: r.identity_id,
        displayName: r.display_name,
        units,
        owner,
        hasContent: r.has_publication || r.placement_count > 0,
      });
    }
    return out;
  }, [interCam, snapshotState.snapshot, parcelById]);

  const pubItems = usePublicationDiscovery(allParcels, contentCam != null, snapshotState.invalidationEpoch);
  const artworkItems = useArtworkPlacementDiscovery(contentCam, snapshotState.invalidationEpoch);
  // Macro #26 · Increment 2 — territories with a VALID published 3D scene get an
  // ENTER 3D affordance, but ONLY in the INTERMEDIATE band (Level 2). WebGL is
  // never loaded until the visitor explicitly enters.
  const [entered3d, setEntered3d] = useState<Public3DTarget | null>(null);
  const enter3dItems = useMemo<Enter3DItem[]>(() => {
    if (interCam == null) return [];
    const out: Enter3DItem[] = [];
    for (const it of pubItems) {
      const scene = it.snapshot.scene_3d;
      if (!enter3dEligible(scene)) continue;
      // Per-work-art world footprints, so the "3D" glow mark lands over EACH
      // published artwork (not once over the whole territory).
      const artworks: Array<{ x: number; y: number; w: number; h: number }> = [];
      for (const wa of scene?.work_arts ?? []) {
        const bb = sceneBoundsXY([wa]);
        if (bb) artworks.push({ x: bb.minX, y: bb.minY, w: bb.maxX - bb.minX + 1, h: bb.maxY - bb.minY + 1 });
      }
      out.push({
        identityId: it.renderable.identityId,
        displayName: it.snapshot.display_name,
        units: it.renderable.units,
        voxelCount: scene?.voxel_count ?? scene?.work_arts?.length ?? 0,
        artworks,
      });
    }
    return out;
  }, [interCam, pubItems]);
  // Macro #26 · Contest polish — a published 3D territory paints its BIRD'S-EYE
  // voxel projection onto the Grid so its parcels visibly occupy space (instead
  // of reading empty behind the Enter-3D pill). Shown in BOTH the intermediate
  // and detail bands via contentCam; the pill affordance stays in its own layer.
  const voxel3dItems = useMemo<Voxel3DFootprintItem[]>(() => {
    if (contentCam == null) return [];
    const out: Voxel3DFootprintItem[] = [];
    for (const it of pubItems) {
      const scene = it.snapshot.scene_3d;
      if (!enter3dEligible(scene) || !scene) continue;
      const cells = topDownProjection(scene.work_arts);
      if (cells.length === 0) continue;
      out.push({ identityId: it.renderable.identityId, units: it.renderable.units, cells });
    }
    return out;
  }, [contentCam, pubItems]);
  // Leaving Level 2 (zoom to overview/detail) closes the 3D overlay so the live
  // Grid LOD stays authoritative and WebGL is torn down.
  useEffect(() => {
    if (interCam == null) setEntered3d(null);
  }, [interCam]);
  useEffect(() => {
    brandRenderablesRef.current = brandRenderables;
    dirtyRef.current = true;
  }, [brandRenderables]);
  const buildBrandLayer = useCallback((): BrandImageLayerItem[] => {
    const items: BrandImageLayerItem[] = [];
    for (const r of brandRenderablesRef.current) {
      const image = brandTextures.get(r.identityId);
      if (!image) continue; // only decoded, ready textures paint
      items.push({ units: r.units, image, imageWidth: r.imageWidth, imageHeight: r.imageHeight });
    }
    return items;
  }, [brandTextures]);

  const range = useMemo(() => snapshotRange(Object.values(tiles)), [tiles]);
  const mapStatus = useMemo(() => deriveMapStatus(Object.values(tiles)), [tiles]);

  const degraded = useMemo(() => {
    // Reviewer P0-10: tile.reconciled==false is degraded even if data_status is "ok".
    const lg = statsUi.lastGood;
    if (lg && (lg.data_status === "degraded" || !lg.reconciled)) {
      return true;
    }
    for (const t of Object.values(tiles)) {
      if (!t.hasSnapshot) continue;
      if (t.dataStatus === "degraded") return true;
      if (t.reconciled === false) return true;
    }
    return false;
  }, [statsUi, tiles]);

  // ---- Publication path (calls into production reducer directly) ----
  const publish = useCallback(
    (tx: 0 | 1, ty: 0 | 1, mySeq: number, result: Parameters<typeof applyPublication>[1]["result"]) => {
      setTiles((prev) => {
        const key = `${tx}:${ty}`;
        const cur = prev[key];
        const next = applyPublication(cur, {
          requestSeq: mySeq,
          result,
          now: Date.now(),
        });
        if (next === cur) return prev;
        return { ...prev, [key]: next };
      });
    },
    [],
  );

  const reloadTile = useCallback(
    async (tx: 0 | 1, ty: 0 | 1, cycle?: RefreshCycle) => {
      const key = `${tx}:${ty}`;
      const prev = abortsRef.current.get(key);
      if (prev) prev.abort();
      const ac = new AbortController();
      abortsRef.current.set(key, ac);

      let mySeq = 0;
      setTiles((prevT) => {
        const cur = prevT[key];
        mySeq = cur.requestSeq + 1;
        return { ...prevT, [key]: beginRefresh(cur, mySeq) };
      });
      cycle?.addRequest(key);

      const result = await fetchTileFull(api, { tileX: tx, tileY: ty }, ac.signal);
      publish(tx, ty, mySeq, result);
      cycle?.settleRequest(key);
    },
    [api, publish],
  );

  /** Begin a fresh refresh cycle: all four tiles as base batch + skew budget. */
  const beginRefreshCycle = useCallback(() => {
    const c = cycleRef.current;
    c.beginCycle();
    skewBudgetRef.current.beginCycle(c.id);
    c.onSettle(() => {
      // Shell loading gate — first cycle settle == initial parcels painted.
      if (!firstReadyFiredRef.current) {
        firstReadyFiredRef.current = true;
        onFirstReadyRef.current?.();
      }
      // Evaluate skew only AFTER the base batch settles.
      const arr = Object.values(tilesRef.current);
      const r = snapshotRange(arr);
      if (!r || r.uniform) return;
      if (!c.claimSkewRetry()) return;
      for (const t of arr) {
        if (!t.hasSnapshot) continue;
        if (t.state !== "ready" && t.state !== "stale") continue;
        if ((t.asOfBlockMax ?? -1) < r.bmax) {
          void reloadTile(t.tileX, t.tileY /* retry NOT tracked in cycle */);
        }
      }
    });
    for (const co of allTileCoords()) void reloadTile(co.tileX, co.tileY, c);
  }, [reloadTile]);

  const reloadAllTiles = beginRefreshCycle; // legacy alias
  void reloadAllTiles;

  // HOTFIX purchase-indexed-grid-refresh — when the shell bumps `refreshSignal`
  // (e.g. a purchase was authoritatively indexed) run the existing
  // authoritative refresh cycle so the newly indexed parcel's tiles reload
  // from the backend. Edge-detected via a ref so the initial mount value never
  // refreshes and each distinct bump triggers exactly one cycle (no loops).
  const prevRefreshSignalRef = useRef<number | undefined>(refreshSignal);
  useEffect(() => {
    if (refreshSignalChanged(prevRefreshSignalRef.current, refreshSignal)) {
      prevRefreshSignalRef.current = refreshSignal;
      beginRefreshCycle();
    } else {
      prevRefreshSignalRef.current = refreshSignal;
    }
  }, [refreshSignal, beginRefreshCycle]);

  // ---- Stats poll ----
  const refreshStats = useCallback(async () => {
    setStatsUi((prev) => nextStatsState(prev, { type: "loading-start" }));
    try {
      const s = await api.stats();
      const prevBlock = statsUiRef.current.lastGood?.data.indexed_through_block ?? null;
      setStatsUi((prev) =>
        nextStatsState(prev, {
          type: "success",
          data: {
            data: s.stats,
            chain_id: s.meta.chain_id,
            reconciled: !!s.meta.reconciled,
            data_status: s.meta.data_status,
          },
        }),
      );
      const block = s.stats.indexed_through_block;
      setTiles((prevT) => {
        const arr = Object.values(prevT);
        const marked = markStaleAgainstStats(arr, block);
        const next = { ...prevT };
        for (const t of marked) next[`${t.tileX}:${t.tileY}`] = t;
        return next;
      });
      // Advance to a fresh cycle iff indexed block advanced.
      if (block != null && block !== prevBlock) {
        beginRefreshCycle();
      } else {
        scheduleVisibleRefresh();
      }
    } catch (err) {
      if (err instanceof ChainMismatchError) {
        setStatsUi((prev) =>
          nextStatsState(prev, { type: "chain-mismatch", got: err.actual, expected: err.expected }),
        );
        return;
      }
      if (err instanceof ViewerApiError && err.aborted) return;
      const msg = err instanceof Error ? err.message : String(err);
      setStatsUi((prev) => nextStatsState(prev, { type: "failure", message: msg }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, beginRefreshCycle]);

  // ---- Visible-tile refresh scheduling (debounced) ----
  const scheduleVisibleRefresh = useCallback(() => {
    if (refreshTimerRef.current != null) window.clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = window.setTimeout(() => {
      refreshTimerRef.current = null;
      const wrap = wrapRef.current;
      if (!wrap) return;
      const rect = wrap.getBoundingClientRect();
      const cam = cameraRef.current;
      const view = {
        x: cam.x,
        y: cam.y,
        w: rect.width / cam.scale,
        h: rect.height / cam.scale,
      };
      const needed = visibleTiles(view);
      for (const c of needed) {
        const key = `${c.tileX}:${c.tileY}`;
        const t = (tilesRef.current)[key];
        if (!t) continue;
        // Fetch when stale, missing, or errored (only if no prior snapshot).
        if (t.state === "stale" || t.state === "missing") {
          void reloadTile(c.tileX, c.tileY);
        } else if (t.state === "error" && !t.hasSnapshot) {
          void reloadTile(c.tileX, c.tileY);
        }
      }
    }, REFRESH_DEBOUNCE_MS);
  }, [reloadTile]);

  // Keep a ref mirror of tiles + stats so debounced callbacks see the latest.
  const tilesRef = useRef(tiles);
  useEffect(() => {
    tilesRef.current = tiles;
  }, [tiles]);
  const statsUiRef = useRef(statsUi);
  useEffect(() => {
    statsUiRef.current = statsUi;
  }, [statsUi]);

  // Mount: initial cycle + stats poll + visibilitychange.
  useEffect(() => {
    beginRefreshCycle();
    void refreshStats();
    const iv = window.setInterval(() => void refreshStats(), STATS_POLL_MS);
    const vis = () => {
      if (document.visibilityState === "visible") void refreshStats();
    };
    document.addEventListener("visibilitychange", vis);
    // WO — capture the (stable, never-reassigned) aborts map so the cleanup
    // aborts the SAME instance the effect set up, satisfying the ref-in-cleanup
    // rule without changing behaviour.
    const aborts = abortsRef.current;
    return () => {
      window.clearInterval(iv);
      document.removeEventListener("visibilitychange", vis);
      if (refreshTimerRef.current != null) window.clearTimeout(refreshTimerRef.current);
      if (fitTweenRef.current != null) cancelAnimationFrame(fitTweenRef.current);
      aborts.forEach((a) => a.abort());
      aborts.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // (Skew retry is now claimed inside RefreshCycle.onSettle — see beginRefreshCycle.)

  // WO-P5 iter4 §LAYOUT-SHIFT-FIX — synchronous first fit + first
  // render BEFORE the browser paints the DOM. The pre-iter4 flow
  // relied on a post-paint `useEffect` for `initFit`, which meant
  // the very first browser paint showed the canvas either as a
  // stretched 300×150 default (the canvas element's HTML default
  // backing store) or as a world painted from the identity camera
  // (scale=1, offset=0) — reading as a "rectangular grid" that
  // then snapped to the fitted square. Doing the fit + first
  // render inside `useLayoutEffect` guarantees the first pixel
  // the user sees is already the square, correctly framed and
  // DPR-sized. Deliberately keeps `[]` deps so this only runs on
  // mount — the render-loop effect below owns all subsequent
  // repaints and resizes.
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    if (didInitFitRef.current) return;
    const rect = wrap.getBoundingClientRect();
    if (rect.width < 1 || rect.height < 1) return;
    const dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
    dprRef.current = dpr;
    cameraRef.current = clampCamera(
      fitAtScale(rect.width, rect.height, initialScale(rect.width, rect.height)),
      rect.width,
      rect.height,
    );
    lastFitSizeRef.current = { w: rect.width, h: rect.height };
    didInitFitRef.current = true;
    // Prime the canvas backing store to the correct pixel size
    // AND paint the first frame — all before the browser commits
    // the DOM tree. From the user's perspective the canvas mounts
    // already showing the fitted square.
    const size = resizeCanvasForDpr(canvas, dpr);
    const ctx = canvas.getContext("2d");
    if (ctx) {
      render({
        ctx,
        cssWidth: size.cssWidth,
        cssHeight: size.cssHeight,
        dpr,
        camera: cameraRef.current,
        parcels: allParcels,
        hoveredId,
        selectedId,
        degraded: !!degraded,
        selectionOverlay,
        zoneOverlay: zoneOverlay ? { manifest: zoneOverlay } : null,
        revealZones: !!revealZones,
        revealHoverRole: null,
        brandImages: buildBrandLayer(),
      });
      // Mirror the camera dataset the same way the RAF loop does,
      // so downstream E2E scripts that read these immediately after
      // mount observe consistent values.
      canvas.dataset.cameraX = String(cameraRef.current.x);
      canvas.dataset.cameraY = String(cameraRef.current.y);
      canvas.dataset.cameraScale = String(cameraRef.current.scale);
      canvas.dataset.canvasCssWidth = String(size.cssWidth);
      canvas.dataset.canvasCssHeight = String(size.cssHeight);
      canvas.dataset.fitReady = "true";
    }
    dirtyRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Render loop ----
  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const initFit = () => {
      const rect = wrap.getBoundingClientRect();
      if (rect.width < 1 || rect.height < 1) return; // layout not ready
      cameraRef.current = clampCamera(fitAtScale(rect.width, rect.height, initialScale(rect.width, rect.height)), rect.width, rect.height);
      lastFitSizeRef.current = { w: rect.width, h: rect.height };
      dirtyRef.current = true;
    };
    // WO#5 — fit ONLY the first time this effect runs after mount.
    // Subsequent re-runs (deps change: allParcels / hovered / selected /
    // degraded / selectionOverlay) leave the camera authority intact.
    if (!didInitFitRef.current) {
      initFit();
      // Only mark as "initialised" once we actually managed to fit
      // against a real layout. If the wrap was still 0×0 at first
      // render, defer initialisation to the ResizeObserver.
      if (lastFitSizeRef.current) didInitFitRef.current = true;
    }
    const ro = new ResizeObserver(() => {
      dprRef.current = window.devicePixelRatio || 1;
      dirtyRef.current = true;
      // WO#5 — refit the camera on genuine wrap resizes unless the
      // user has taken authority of the camera.
      const rect = wrap.getBoundingClientRect();
      if (rect.width < 1 || rect.height < 1) return;
      const last = lastFitSizeRef.current;
      // 3% delta gate → suppress no-op observer firings.
      const changed =
        !last ||
        Math.abs(rect.width - last.w) / Math.max(1, last.w) > 0.03 ||
        Math.abs(rect.height - last.h) / Math.max(1, last.h) > 0.03;
      if (!changed) return;
      if (userAdjustedCameraRef.current) {
        // User owns the camera — just re-clamp to the new bounds so
        // pan/zoom limits track the current viewport, but do NOT
        // refit. This preserves their exact framing.
        cameraRef.current = clampCamera(cameraRef.current, rect.width, rect.height);
        lastFitSizeRef.current = { w: rect.width, h: rect.height };
        return;
      }
      initFit();
      if (!didInitFitRef.current) didInitFitRef.current = true;
    });
    ro.observe(wrap);
    // WO §MULTI-MONITOR — a window dragged between screens of different
    // resolution / pixel density may NOT emit a "resize" event when its CSS
    // size is unchanged, leaving the canvas backing store stale (blurry /
    // distorted). A recursive matchMedia(resolution) listener catches every
    // DPR change reliably across browsers. On any screen change we resync the
    // backing store (dirty) and refit the camera to the current wrap unless
    // the user owns the camera.
    let mqlOff: (() => void) | null = null;
    const syncToScreen = () => {
      dprRef.current = window.devicePixelRatio || 1;
      dirtyRef.current = true;
      const rect = wrap.getBoundingClientRect();
      if (rect.width >= 1 && rect.height >= 1 && !userAdjustedCameraRef.current) {
        cameraRef.current = clampCamera(
          fitAtScale(rect.width, rect.height, initialScale(rect.width, rect.height)),
          rect.width,
          rect.height,
        );
        lastFitSizeRef.current = { w: rect.width, h: rect.height };
      }
    };
    const attachDprListener = () => {
      if (typeof window.matchMedia !== "function") return;
      const dpr = window.devicePixelRatio || 1;
      const mql = window.matchMedia(`(resolution: ${dpr}dppx)`);
      const onChange = () => {
        syncToScreen();
        attachDprListener();
      };
      mql.addEventListener("change", onChange, { once: true });
      mqlOff = () => mql.removeEventListener("change", onChange);
    };
    const onDpr = () => syncToScreen();
    window.addEventListener("resize", onDpr);
    attachDprListener();

    const loop = () => {
      // WO-CONTEST §BAND-FADE (Claudio) — ease the 3 perimeter bands' glow
      // toward 0 while a pan drag is HELD, and back toward 1 the instant the
      // button is RELEASED. Driven by `panActiveRef` (pointer state), NOT
      // camera motion, so holding still mid-drag keeps them off and inertia
      // after release never delays the fade-in. Runs every RAF tick (the loop
      // reschedules continuously) → smooth + symmetric in both directions.
      {
        const nowTs = typeof performance !== "undefined" ? performance.now() : Date.now();
        const prevTs = bandGlowTsRef.current;
        bandGlowTsRef.current = nowTs;
        const glowTarget = panActiveRef.current ? 0 : 1;
        if (bandGlowRef.current !== glowTarget) {
          const dt = prevTs == null ? 16 : Math.min(48, nowTs - prevTs);
          const glowStep = dt / 350; // ms for a full 0<->1 fade (medium)
          bandGlowRef.current =
            bandGlowRef.current < glowTarget
              ? Math.min(glowTarget, bandGlowRef.current + glowStep)
              : Math.max(glowTarget, bandGlowRef.current - glowStep);
          dirtyRef.current = true; // repaint so the fade animates to completion
        }
      }
      if (dirtyRef.current) {
        const size = resizeCanvasForDpr(canvas, dprRef.current);
        const ctx = canvas.getContext("2d");
        if (ctx) {
          const brandLayer = buildBrandLayer();
          const pc = cameraRef.current;
          const lc = lastPaintedCamRef.current;
          const moved = !lc || lc.x !== pc.x || lc.y !== pc.y || lc.scale !== pc.scale;
          render({
            ctx,
            cssWidth: size.cssWidth,
            cssHeight: size.cssHeight,
            dpr: dprRef.current,
            camera: cameraRef.current,
            parcels: allParcels,
            hoveredId,
            selectedId,
            degraded: !!degraded,
            selectionOverlay,
            zoneOverlay: zoneOverlay ? { manifest: zoneOverlay } : null,
            revealZones: revealZonesRef.current,
            revealHoverRole: revealHoverRoleRef.current,
            brandImages: brandLayer,
            quality: moved ? "fast" : "full",
            glowIntensity: bandGlowRef.current,
          });
          lastPaintedCamRef.current = { x: pc.x, y: pc.y, scale: pc.scale };
          // Motion is ongoing → schedule ONE trailing full-quality repaint so
          // the (non-band) glows return the instant the camera settles.
          if (moved) {
            if (qualityTimerRef.current != null) clearTimeout(qualityTimerRef.current);
            qualityTimerRef.current = window.setTimeout(() => {
              qualityTimerRef.current = null;
              dirtyRef.current = true;
            }, 170);
          }
          canvas.dataset.brandRenderables = String(brandRenderablesRef.current.length);
          canvas.dataset.brandTexturesReady = String(brandLayer.length);
          canvas.dataset.parcelCount = String(allParcels.length);
        }
        // Presentation-only camera surface for E2E coordinate math
        // (Devil #4 pre-authorized by Neo). Mirrors the committed
        // camera used by `render` so screenToWorld/worldToScreen can
        // be reproduced from the DOM without touching internals.
        canvas.dataset.cameraX = String(cameraRef.current.x);
        canvas.dataset.cameraY = String(cameraRef.current.y);
        canvas.dataset.cameraScale = String(cameraRef.current.scale);
        canvas.dataset.canvasCssWidth = String(size.cssWidth);
        canvas.dataset.canvasCssHeight = String(size.cssHeight);
        // WO-P5 iter4 — reveal the canvas as soon as the first
        // valid frame has painted (guards the edge case where the
        // pre-paint `useLayoutEffect` above bailed out because the
        // wrap had zero dimensions at mount).
        if (canvas.dataset.fitReady !== "true") {
          canvas.dataset.fitReady = "true";
        }
        dirtyRef.current = false;
        // PUBLICATION → GRID §5/§7 + #17 LOD bands — publish the committed
        // camera to React per band: DETAIL (>7.0) drives the full publication +
        // artwork overlays (unchanged content); INTERMEDIATE (4.27–7.0) drives
        // the lightweight content-presence layer; OVERVIEW (≤4.27) shows only
        // Grid geography + the frozen LOD0 brand image. Bands are mutually
        // exclusive → exactly one content camera is ever non-null (clean handoff,
        // no overlap/duplication). `artworkOverlayRef` keeps the locate-a-piece
        // bypass forcing detail regardless of band.
        {
          const pc = cameraRef.current;
          const detail = isDetailBand(pc.scale) || !!artworkOverlayRef.current;
          const intermediate = !detail && isIntermediateBand(pc.scale);
          if (detail) {
            const lp = lastPubCamRef.current;
            if (!lp || lp.x !== pc.x || lp.y !== pc.y || lp.scale !== pc.scale) {
              const snap = { x: pc.x, y: pc.y, scale: pc.scale };
              lastPubCamRef.current = snap;
              setPubCam(snap);
            }
            if (lastInterCamRef.current !== null) {
              lastInterCamRef.current = null;
              setInterCam(null);
            }
          } else if (intermediate) {
            const li = lastInterCamRef.current;
            if (!li || li.x !== pc.x || li.y !== pc.y || li.scale !== pc.scale) {
              const snap = { x: pc.x, y: pc.y, scale: pc.scale };
              lastInterCamRef.current = snap;
              setInterCam(snap);
            }
            if (lastPubCamRef.current !== null) {
              lastPubCamRef.current = null;
              setPubCam(null);
            }
          } else {
            if (lastPubCamRef.current !== null) {
              lastPubCamRef.current = null;
              setPubCam(null);
            }
            if (lastInterCamRef.current !== null) {
              lastInterCamRef.current = null;
              setInterCam(null);
            }
          }
        }
        // WO-VRD-33 — keep the frame dirty while Reveal Areas is active so
        // the fluorescent pulse animates smoothly. Idle otherwise (no cost).
        if (revealZonesRef.current) dirtyRef.current = true;
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      if (qualityTimerRef.current != null) clearTimeout(qualityTimerRef.current);
      ro.disconnect();
      window.removeEventListener("resize", onDpr);
      mqlOff?.();
    };
  }, [allParcels, hoveredId, selectedId, degraded, selectionOverlay, buildBrandLayer, zoneOverlay]);

  // ---- Native wheel with { passive:false } — reliable preventDefault ----
  // WO#5 §SCROLL-JACKING — the wheel does NOT trap the page by default.
  //   - Normal wheel (no modifier) → NO preventDefault → the browser
  //     scrolls the outer page (narrative scroll experience). The
  //     viewer canvas simply lets the event bubble.
  //   - Ctrl / Meta (⌘) + wheel     → preventDefault + zoom around the
  //     screen anchor via the canonical `zoomAt` + `clampCamera`
  //     pipeline. Matches the Figma / Google Maps / macOS pinch model
  //     (macOS trackpad pinch synthesizes wheel with ctrlKey=true, so
  //     the same code path handles it naturally — no separate branch).
  //
  // Routing extracted to `./wheel-router` so the WO#5 P0 regression
  // guards (plain-wheel → page-scroll, Ctrl+wheel → Grid-zoom) live
  // as pure vitests. The re-registration with { passive:false } is
  // preserved by the helper.
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    return attachWheelRouting(wrap, {
      getCamera: () => cameraRef.current,
      setCamera: (next) => {
        cameraRef.current = next;
      },
      onUserCameraAuthority: () => {
        userAdjustedCameraRef.current = true;
        cancelFitTween();
      },
      onDirty: () => {
        dirtyRef.current = true;
      },
      onScheduleRefresh: () => scheduleVisibleRefresh(),
    });
  }, [scheduleVisibleRefresh, cancelFitTween]);

  // ---- Deep-link focus (#grid?fx=&fy=&aid=) ----
  // "Locate on Grid" links carry a world coordinate (and optional artwork id).
  // Center the camera on that point at detail LOD so the target surfaces (the
  // Frozen Artwork overlay only mounts past LOD0). Presentation-only: uses the
  // canonical clampCamera; camera math in camera.ts is untouched.
  const applyCameraFocus = useCallback(
    (f: { fx: number; fy: number; aid?: string | null; span: number; ov?: boolean }) => {
      let tries = 0;
      const tick = () => {
        const wrap = wrapRef.current;
        if (!wrap) return;
        const rect = wrap.getBoundingClientRect();
        if (rect.width < 1 || rect.height < 1) {
          if (tries++ < 120) requestAnimationFrame(tick);
          return;
        }
        const span = f.span > 0 ? f.span : 60;
        // #15 Show & Drag — `ov` (overview) relaxes the zoom floor so the
        // camera can pull ALL owned parcels into a panoramic frame; the
        // normal "Locate on Grid" deep-link keeps the tight detail floor.
        const floor = f.ov ? 0.3 : 5;
        const scale = Math.max(floor, Math.min(MAX_ZOOM, Math.min(rect.width, rect.height) / span));
        cameraRef.current = clampCamera(
          { x: f.fx - rect.width / (2 * scale), y: f.fy - rect.height / (2 * scale), scale },
          rect.width,
          rect.height,
        );
        userAdjustedCameraRef.current = true;
        cancelFitTween();
        dirtyRef.current = true;
        scheduleVisibleRefresh();
        if (f.aid) {
          window.setTimeout(() => {
            window.dispatchEvent(new CustomEvent(ARTWORK_SELECT_EVENT, { detail: { artworkId: f.aid } }));
          }, 450);
        }
      };
      requestAnimationFrame(tick);
    },
    [scheduleVisibleRefresh, cancelFitTween],
  );

  useEffect(() => {
    const parseFocus = () => {
      const h = window.location.hash.replace(/^#/, "");
      const qi = h.indexOf("?");
      if (qi < 0) return null;
      const base = h.slice(0, qi);
      if (base !== "grid" && base !== "purchase") return null;
      const p = new URLSearchParams(h.slice(qi + 1));
      const fx = Number(p.get("fx"));
      const fy = Number(p.get("fy"));
      if (!Number.isFinite(fx) || !Number.isFinite(fy)) return null;
      const span = Number(p.get("span"));
      return { fx, fy, aid: p.get("aid"), span: Number.isFinite(span) && span > 0 ? span : 0, ov: p.get("ov") === "1" };
    };
    const initial = parseFocus();
    if (initial) applyCameraFocus(initial);
    const onHash = () => {
      const f = parseFocus();
      if (f) applyCameraFocus(f);
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, [applyCameraFocus]);

  // Macro #20 (Admin Clients) — declarative `focus` prop: frame a world point
  // WITHOUT touching the route hash (used by the embedded client grid).
  useEffect(() => {
    if (focus && Number.isFinite(focus.fx) && Number.isFinite(focus.fy)) {
      applyCameraFocus({ fx: focus.fx, fy: focus.fy, span: focus.span, ov: focus.ov });
    }
  }, [focus, applyCameraFocus]);

  // ---- matchMedia (mobile layout + fine pointer for hover) ----
  useLayoutEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mm = window.matchMedia("(max-width: 720px)");
    const fine = window.matchMedia("(pointer: fine)");
    const upd = () => {
      setIsMobile(mm.matches);
      setIsFinePointer(fine.matches);
    };
    upd();
    mm.addEventListener?.("change", upd);
    fine.addEventListener?.("change", upd);
    return () => {
      mm.removeEventListener?.("change", upd);
      fine.removeEventListener?.("change", upd);
    };
  }, []);

  // MACRO #31 HOTFIX — NATIVE PAGE 3 SCROLL ownership. The canvas uses
  // touch-action:pan-y, so the browser scrolls the narrative natively; we
  // cancel that ONLY for Grid-owned sequences (first touch INSIDE the plane, or
  // any multi-touch pinch) via non-passive preventDefault. Ownership is frozen
  // at the first touch: a sequence that began OUTSIDE the Grid is never claimed
  // later (even if a finger wanders in), and one that began inside keeps the
  // Grid until release. ZERO manual window.scrollBy — the browser owns outside
  // scrolling end-to-end.
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const insideAt = (clientX: number, clientY: number) => {
      const rect = el.getBoundingClientRect();
      const w = screenToWorld(cameraRef.current, clientX - rect.left, clientY - rect.top);
      return isInsideGrid(w.x, w.y);
    };
    const onTouchStart = (e: TouchEvent) => {
      if (touchSeqOwnerRef.current === null) {
        const t = e.touches[0];
        touchSeqOwnerRef.current = decideTouchOwner(null, {
          touchCount: e.touches.length,
          firstTouchInsideGrid: t ? insideAt(t.clientX, t.clientY) : false,
        });
      }
      if (ownerSuppressesNativeScroll(touchSeqOwnerRef.current)) e.preventDefault();
    };
    const onTouchMove = (e: TouchEvent) => {
      if (ownerSuppressesNativeScroll(touchSeqOwnerRef.current)) e.preventDefault();
    };
    const onTouchEnd = (e: TouchEvent) => {
      if (e.touches.length === 0) touchSeqOwnerRef.current = null;
    };
    el.addEventListener("touchstart", onTouchStart, { passive: false });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    el.addEventListener("touchcancel", onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, []);

// ---- Pointer input handlers ----
  const rectCoords = (clientX: number, clientY: number) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    // MACRO #31 HOTFIX — FROZEN INPUT OWNERSHIP. If the page already owns the
    // current touch sequence (a prior pointer went down OUTSIDE the Grid), the
    // Grid ignores EVERY subsequent pointer for the rest of that sequence. A
    // gesture started outside can never be stolen by the Grid later — no second
    // finger, no re-entry, no camera mutation.
    if (outsideScrollRef.current) return;
    cancelFitTween();
    const p = rectCoords(e.clientX, e.clientY);
    const isPrimary = (e.isPrimary as boolean | undefined) !== false;
    // Claudio — single-finger touch that STARTS outside the grid plane scrolls
    // the page instead of panning the Grid (escape hatch so the Territory
    // section is never a touch dead-end). Only engage for the primary touch;
    // a second finger falls through to the normal pinch path below.
    if (e.pointerType === "touch" && isPrimary) {
      const wDown = screenToWorld(cameraRef.current, p.x, p.y);
      if (!isInsideGrid(wDown.x, wDown.y)) {
        outsideScrollRef.current = { pointerId: e.pointerId, lastY: e.clientY };
        // MACRO #31 HOTFIX — do NOT capture or pan: the browser scrolls the
        // page natively (touch-action:pan-y). This marker just keeps the Grid
        // pointer path inert for the rest of this page-owned sequence.
        if (mode === "select" && rectSelRef.current.committed) {
          // Preserve the Inspect/Select "void tap dismisses selection" UX.
          setRectSel((prev) => clearCommitted(prev));
          onClearSelection?.();
          dirtyRef.current = true;
        }
        return;
      }
      outsideScrollRef.current = null;
    }
    // SEP 5.b.2 — Selection mode: primary pointer, primary button, starts
    // a draft when the start world point is INSIDE the grid. In select
    // mode we do NOT enter the pan gesture on primary drag.
    const isPrimaryButton = e.button === 0;
    if (
      mode === "select" &&
      isPrimary &&
      isPrimaryButton &&
      rectSelRef.current.mode === "select"
    ) {
      const w = screenToWorld(cameraRef.current, p.x, p.y);
      if (isInsideGrid(w.x, w.y)) {
        setRectSel((prev) => beginDraft(prev, w.x, w.y));
        setDraftScreen(p);
        try {
          e.currentTarget.setPointerCapture(e.pointerId);
        } catch {
          /* jsdom */
        }
        dirtyRef.current = true;
      } else if (rectSelRef.current.committed) {
        // WO-VRD-31 (Claudio) — start OUTSIDE grid dismisses a live
        // committed selection (the Context drawer closes), mirroring the
        // Inspect void-tap. Presentation/UX only — no coordinate math.
        setRectSel((prev) => clearCommitted(prev));
        onClearSelection?.();
        dirtyRef.current = true;
      }
      // start OUTSIDE grid → no draft, no pan either (mode=select suppresses browse).
      return;
    }
    onPointerDown(
      gestureRef.current,
      { id: e.pointerId, x: p.x, y: p.y, type: e.pointerType as never },
      cameraRef.current,
    );
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* some environments (jsdom) don't implement capture */
    }
  };
  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const p = rectCoords(e.clientX, e.clientY);
    // Claudio — outside-grid touch drag → scroll the page manually (the canvas
    // keeps touch-action:none, so there is no native scroll to fight).
    const os = outsideScrollRef.current;
    if (os && os.pointerId === e.pointerId) {
      // MACRO #31 HOTFIX — PAGE owns this sequence (pointerdown started OUTSIDE
      // the Grid). The browser scrolls the narrative NATIVELY (touch-action:
      // pan-y). The Grid does ZERO here: no manual scroll, no camera, no
      // capture, no gesture-state mutation.
      return;
    }
    // SEP 5.b.2 — Selection-mode drag: update draft on move (world coord,
    // possibly outside grid — endpoint is clamped only at commit time).
    if (mode === "select" && rectSelRef.current.draft) {
      const w = screenToWorld(cameraRef.current, p.x, p.y);
      setRectSel((prev) => updateDraft(prev, w.x, w.y));
      setDraftScreen(p);
      dirtyRef.current = true;
      return;
    }
    // Idle in select mode with no draft → suppress hover/gesture browse work.
    if (mode === "select") return;
    const before = gestureRef.current.mode;
    const upd = onPointerMove(
      gestureRef.current,
      { id: e.pointerId, x: p.x, y: p.y, type: e.pointerType as never },
      cameraRef.current,
    );
    if (upd.camera) {
      const wrap = wrapRef.current!;
      const rect = wrap.getBoundingClientRect();
      cameraRef.current = clampCamera(upd.camera, rect.width, rect.height);
      userAdjustedCameraRef.current = true;
      panActiveRef.current = true; // WO-CONTEST §BAND-FADE — pan drag is live
      dirtyRef.current = true;
      scheduleVisibleRefresh();
      return;
    }
    // Idle (no drag) → hover for fine pointers only.
    if (before === "idle" && isFinePointer && e.pointerType === "mouse") {
      const w = screenToWorld(cameraRef.current, p.x, p.y);
      const hit = hitTest(spatial, w.x, w.y);
      const nextId = hit.parcel ? hit.parcel.id : null;
      if (nextId !== hoveredId) {
        setHoveredId(nextId);
        dirtyRef.current = true;
      }
      // Tooltip position stays SCREEN space (absolute DOM inside wrap).
      setHoverPos(nextId != null ? { x: p.x, y: p.y } : null);
      // WO#5 §HUD — world-space readout for the coordinate HUD. Emitted
      // on EVERY move whose world point falls inside the [0,1000)²
      // territory, independent of whether a parcel is under the cursor.
      const insideWorld =
        w.x >= 0 && w.x < 1000 && w.y >= 0 && w.y < 1000;
      setHoverWorld(insideWorld ? { x: w.x, y: w.y } : null);
    }
  };
  const lastTapRef = useRef<{ t: number; sx: number; sy: number }>({ t: 0, sx: 0, sy: 0 });
  const finishPointer = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const p = rectCoords(e.clientX, e.clientY);
    panActiveRef.current = false; // WO-CONTEST §BAND-FADE — release → bands fade back in
    // Claudio — end of an outside-grid page-scroll gesture.
    if (outsideScrollRef.current && outsideScrollRef.current.pointerId === e.pointerId) {
      outsideScrollRef.current = null;
      return;
    }
    // SEP 5.b.2 — Selection-mode commit on pointerup.
    if (mode === "select" && rectSelRef.current.draft) {
      // Ensure the LATEST world coord is applied before commit.
      const w = screenToWorld(cameraRef.current, p.x, p.y);
      setRectSel((prev) => {
        if (!prev.draft) return prev;
        const staged = updateDraft(prev, w.x, w.y);
        const next = commitDraft(staged);
        if (next.committed) onCommitSelection?.(next.committed);
        return next;
      });
      setDraftScreen(null);
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* nop */
      }
      dirtyRef.current = true;
      return;
    }
    const res = onPointerUp(
      gestureRef.current,
      { id: e.pointerId, x: p.x, y: p.y, type: e.pointerType as never },
      cameraRef.current,
    );
    if (res.tap) {
      // MACRO #31 — double-tap zoom (touch/mouse), anchored at the tap point,
      // via the SAME zoomAt+clampCamera pipeline as wheel/pinch/+/-. A single
      // tap still selects; the 2nd tap within 300ms & ~28px zooms instead.
      const now = typeof performance !== "undefined" ? performance.now() : Date.now();
      const lt = lastTapRef.current;
      const isDouble =
        now - lt.t < 300 &&
        Math.hypot(res.tap.sx - lt.sx, res.tap.sy - lt.sy) < 28;
      if (isDouble) {
        const wrap = wrapRef.current;
        if (wrap) {
          cancelFitTween();
          const rect = wrap.getBoundingClientRect();
          const newScale = cameraRef.current.scale * 1.8;
          cameraRef.current = clampCamera(
            zoomAt(cameraRef.current, res.tap.sx, res.tap.sy, newScale),
            rect.width,
            rect.height,
          );
          userAdjustedCameraRef.current = true;
          dirtyRef.current = true;
          scheduleVisibleRefresh();
          onCameraChange?.({ scale: cameraRef.current.scale });
        }
        lastTapRef.current = { t: 0, sx: 0, sy: 0 };
      } else {
        lastTapRef.current = { t: now, sx: res.tap.sx, sy: res.tap.sy };
        const w = screenToWorld(cameraRef.current, res.tap.sx, res.tap.sy);
        handleTap(w.x, w.y);
      }
    }
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* nop */
    }
  };
  const handlePointerCancel = () => {
    panActiveRef.current = false; // WO-CONTEST §BAND-FADE — cancel → bands fade back in
    outsideScrollRef.current = null;
    // SEP 5.b.2 — Cancel any active draft; DO NOT commit.
    if (rectSelRef.current.draft) {
      setRectSel((prev) => cancelDraft(prev));
      dirtyRef.current = true;
    }
    setDraftScreen(null);
    onPointerCancel(gestureRef.current);
  };

  // MACRO #18 — pointer arbiter shared by every interactive published-content
  // anchor (LINK / SOCIAL / CONTACTS / PDF). Anchors feed their pointer stream
  // into the SAME canonical Grid gesture machine, so a tap activates the action
  // once while a real drag (past TAP_SLOP_PX) pans the Grid and never activates.
  const contentPointerArbiter = useMemo<ContentPointerArbiter>(() => {
    const local = (clientX: number, clientY: number) => {
      const rect = canvasRef.current?.getBoundingClientRect();
      return { x: clientX - (rect?.left ?? 0), y: clientY - (rect?.top ?? 0) };
    };
    return {
      down: (e) => {
        cancelFitTween();
        const p = local(e.clientX, e.clientY);
        onPointerDown(gestureRef.current, { id: e.pointerId, x: p.x, y: p.y, type: e.pointerType as never }, cameraRef.current);
      },
      move: (e) => {
        const p = local(e.clientX, e.clientY);
        const upd = onPointerMove(gestureRef.current, { id: e.pointerId, x: p.x, y: p.y, type: e.pointerType as never }, cameraRef.current);
        if (upd.camera) {
          const wrap = wrapRef.current;
          if (wrap) {
            const r = wrap.getBoundingClientRect();
            cameraRef.current = clampCamera(upd.camera, r.width, r.height);
            userAdjustedCameraRef.current = true;
            panActiveRef.current = true; // WO-CONTEST §BAND-FADE — content-anchor pan drag is live
            dirtyRef.current = true;
            scheduleVisibleRefresh();
          }
        }
      },
      up: (e) => {
        panActiveRef.current = false; // WO-CONTEST §BAND-FADE — release → bands fade back in
        const p = local(e.clientX, e.clientY);
        const res = onPointerUp(gestureRef.current, { id: e.pointerId, x: p.x, y: p.y, type: e.pointerType as never }, cameraRef.current);
        // res.tap present ⇒ movement stayed below TAP_SLOP_PX ⇒ activate the
        // anchor action. We deliberately do NOT call handleTap: the anchor owns
        // the tap, so no parcel selection fires under an actionable element.
        return Boolean(res.tap);
      },
      cancel: () => {
        panActiveRef.current = false; // WO-CONTEST §BAND-FADE — cancel → bands fade back in
        onPointerCancel(gestureRef.current);
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleVisibleRefresh]);

  // ---- SEP 5.b.2 Escape key cancels an active draft (§L) ----
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (!rectSelRef.current.draft) return;
      setRectSel((prev) => cancelDraft(prev));
      setDraftScreen(null);
      dirtyRef.current = true;
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleTap = (wx: number, wy: number) => {
    const hit = hitTest(spatial, wx, wy);
    // Tap counts as user interaction — freeze camera framing.
    userAdjustedCameraRef.current = true;
    // Bump generation BEFORE any early return — any prior async result is invalidated.
    const gen = selectionRef.current.begin();
    if (hit.conflict) {
      setConflict(hit.candidates);
      setSelectedId(null);
      setDetail(null);
      setCellUi(null);
      dirtyRef.current = true;
      return;
    }
    setConflict(null);
    if (hit.parcel) {
      setCellUi(null);
      setSelectedId(hit.parcel.id);
      void loadDetail(hit.parcel, gen);
    } else {
      setDetail(null);
      setSelectedId(null);
      void confirmEmpty(wx, wy, gen);
    }
    dirtyRef.current = true;
  };

  async function confirmEmpty(wx: number, wy: number, gen: number) {
    const ix = Math.floor(wx);
    const iy = Math.floor(wy);
    // Reviewer P1-2: never call /cell for coordinates outside the world.
    if (!isInsideWorld(ix, iy)) {
      if (!selectionRef.current.isCurrent(gen)) return;
      setCellUi({ kind: "outside", x: ix, y: iy });
      return;
    }
    const tx = (ix >= 500 ? 1 : 0) as 0 | 1;
    const ty = (iy >= 500 ? 1 : 0) as 0 | 1;
    const t = tiles[`${tx}:${ty}`];
    if (!t || !t.hasSnapshot) {
      if (!selectionRef.current.isCurrent(gen)) return;
      setCellUi({ kind: "loading-map", x: ix, y: iy });
      return;
    }
    if (!selectionRef.current.isCurrent(gen)) return;
    setCellUi({ kind: "confirming", x: ix, y: iy });
    try {
      const resp = await api.cell(ix, iy);
      if (!selectionRef.current.isCurrent(gen)) return;
      if (resp.parcel) {
        // Promote to parcel selection under a new generation.
        const g2 = selectionRef.current.begin();
        setSelectedId(resp.parcel.id);
        setCellUi(null);
        void loadDetail(resp.parcel, g2);
      } else {
        setCellUi({ kind: "empty", x: ix, y: iy, asOf: resp.meta.as_of_block });
      }
    } catch {
      if (!selectionRef.current.isCurrent(gen)) return;
      setCellUi({ kind: "confirm-error", x: ix, y: iy });
    }
  }

  async function loadDetail(base: Parcel, gen: number) {
    setCellUi(null);
    setDetail({ parcel: base, asOfBlock: null, reconciled: false });
    try {
      const r = await api.parcel(base.id);
      if (!selectionRef.current.isCurrent(gen)) return;
      setDetail({ parcel: r.parcel, asOfBlock: r.meta.as_of_block, reconciled: r.meta.reconciled });
    } catch (err) {
      if (!selectionRef.current.isCurrent(gen)) return;
      setDetail({
        parcel: base,
        asOfBlock: null,
        reconciled: false,
        fetchError: err instanceof ViewerApiError ? err.message : String(err),
      });
    }
  }

  // WO-VRD-11 §SNAP-FIT — smoothly interpolate the camera from its current
  // state to `target` with an ease-out cubic. Honours prefers-reduced-motion
  // (instant). Presentation-only (interpolates already-valid camera states).
  const animateCameraTo = useCallback(    (target: Camera, duration = 460) => {
      cancelFitTween();
      const start = { ...cameraRef.current };
      const t0 =
        typeof performance !== "undefined" ? performance.now() : Date.now();
      const ease = (t: number) => 1 - Math.pow(1 - t, 3);
      const step = (nowArg?: number) => {
        const now =
          nowArg ??
          (typeof performance !== "undefined" ? performance.now() : Date.now());
        const t = Math.min(1, (now - t0) / duration);
        const k = ease(t);
        cameraRef.current = {
          x: start.x + (target.x - start.x) * k,
          y: start.y + (target.y - start.y) * k,
          scale: start.scale + (target.scale - start.scale) * k,
        };
        dirtyRef.current = true;
        onCameraChange?.({ scale: cameraRef.current.scale });
        if (t < 1) {
          fitTweenRef.current = requestAnimationFrame(step);
        } else {
          cameraRef.current = { ...target };
          fitTweenRef.current = null;
          dirtyRef.current = true;
          scheduleVisibleRefresh();
          onCameraChange?.({ scale: target.scale });
        }
      };
      fitTweenRef.current = requestAnimationFrame(step);
    },
    [cancelFitTween, onCameraChange, scheduleVisibleRefresh],
  );

  const onFitGrid = useCallback(() => {
    const wrap = wrapRef.current!;
    const rect = wrap.getBoundingClientRect();
    const target = fitAtScale(rect.width, rect.height, initialScale(rect.width, rect.height));
    lastFitSizeRef.current = { w: rect.width, h: rect.height };
    // Fit is an explicit "give the camera back to the viewport" — the
    // camera can once again refit on subsequent resizes.
    userAdjustedCameraRef.current = false;
    // Instant (no tween) for reduced-motion users AND under WebDriver
    // (Playwright), so the e2e suite reads a deterministic fitted camera
    // immediately — real browsers get the smooth glide.
    const reduce =
      (typeof window !== "undefined" &&
        window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches) ||
      (typeof navigator !== "undefined" &&
        (navigator as Navigator & { webdriver?: boolean }).webdriver === true);
    if (reduce) {
      cancelFitTween();
      cameraRef.current = target;
      dirtyRef.current = true;
      scheduleVisibleRefresh();
      onCameraChange?.({ scale: target.scale });
    } else {
      // WO-VRD-11 §SNAP-FIT — glide home instead of snapping instantly.
      animateCameraTo(target);
    }
  }, [cancelFitTween, scheduleVisibleRefresh, onCameraChange, animateCameraTo]);

  // H2.3 WO#2 §8 — programmatic zoom around viewport center.
  // Uses the SAME canonical `zoomAt` + `clampCamera` pipeline that the
  // wheel/pinch handlers use (see camera.ts). No new math introduced.
  const zoomByFactor = useCallback(
    (factor: number) => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      cancelFitTween();
      const rect = wrap.getBoundingClientRect();
      const sx = rect.width / 2;
      const sy = rect.height / 2;
      const newScale = cameraRef.current.scale * factor;
      cameraRef.current = clampCamera(
        zoomAt(cameraRef.current, sx, sy, newScale),
        rect.width,
        rect.height,
      );
      userAdjustedCameraRef.current = true;
      dirtyRef.current = true;
      scheduleVisibleRefresh();
      onCameraChange?.({ scale: cameraRef.current.scale });
    },
    [onCameraChange, cancelFitTween, scheduleVisibleRefresh],
  );

  useImperativeHandle(
    ref,
    (): GridViewerHandle => ({
      zoomIn: () => zoomByFactor(1.25),
      zoomOut: () => zoomByFactor(1 / 1.25),
      fitGrid: () => onFitGrid(),
      getScale: () => cameraRef.current.scale,
    }),
    [zoomByFactor, onFitGrid],
  );

  // WO#5 §HUD — emit the WORLD-space hover to the shell. The HUD
  // reads truthful territory coordinates (0..1000), never screen px.
  // `null` when the pointer is outside the world or has left the wrap.
  useEffect(() => {
    if (!onHoverChange) return;
    onHoverChange(hoverWorld);
  }, [hoverWorld, onHoverChange]);

  // H2.3 WO#2 §9 — emit initial camera scale once mounted so the HUD
  // can render a real zoom value from t0 (subsequent updates flow via
  // wheel/pinch/fit/zoomIn/Out — see viewer/gesture.ts wheel handler
  // which is NOT modified; we piggy-back via a ref-polled RAF below).
  const lastEmittedScale = useRef<number>(NaN);
  useEffect(() => {
    if (!onCameraChange) return;
    let raf = 0;
    const tick = () => {
      const s = cameraRef.current.scale;
      if (s !== lastEmittedScale.current) {
        lastEmittedScale.current = s;
        onCameraChange({ scale: s });
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [onCameraChange]);

  // H2.3 WO#3a §Context — Emit the CANONICAL viewer context state
  // whenever the underlying browse-mode selection sources change. This
  // is the single push channel; the shell subscribes via
  // `onContextStateChange` and composes purchase on its own side (see
  // `ui/grid-context.ts`). Structural equality guard prevents effect
  // churn when unrelated state (e.g. tile publications) re-renders.
  const contextState = useMemo<ViewerContextState>(
    () =>
      deriveViewerContextState({
        detail,
        cellUi,
        conflict,
        chainMismatch: statsUi.chainMismatch != null,
        hasAnySnapshot: Object.values(tiles).some((t) => t.hasSnapshot),
        refreshError: statsUi.refreshError ?? null,
      }),
    [detail, cellUi, conflict, statsUi.chainMismatch, statsUi.refreshError, tiles],
  );
  const lastEmittedContext = useRef<ViewerContextState | null>(null);
  useEffect(() => {
    if (!onContextStateChange) return;
    const prev = lastEmittedContext.current;
    if (prev && contextStatesEqual(prev, contextState)) return;
    lastEmittedContext.current = contextState;
    onContextStateChange(contextState);
  }, [contextState, onContextStateChange]);

  // WO-VRD-9 — derive the compact territory sync level and push it to the
  // shell so the desktop top toolbar can render the LIVE/CACHED/CHAIN badge
  // next to the "TERRITORY" header. Same truth source as the in-canvas
  // badge below (which now only renders on mobile).
  const territoryStatusLevel: "live" | "cached" | "chain" = useMemo(() => {
    if (statsUi.chainMismatch) return "chain";
    if (degraded || statsUi.refreshError || mapStatus.kind !== "ready")
      return "cached";
    return "live";
  }, [statsUi.chainMismatch, statsUi.refreshError, degraded, mapStatus.kind]);
  useEffect(() => {
    onStatusChange?.(territoryStatusLevel);
  }, [territoryStatusLevel, onStatusChange]);

  // ---- Header rendering ----
  const stats = statsUi.lastGood?.data ?? null;

  return (
    <div data-testid="grid-viewer" style={styles.root(isMobile, layout)}>
      {chrome === "internal" && (
        <>
          <header style={styles.header} data-testid="grid-header">
            <div style={styles.title}>THE GRID</div>
            <div style={styles.stats}>
              {statsUi.loading && !stats && <span data-testid="hdr-loading">Loading stats…</span>}
              {statsUi.refreshError && stats && (
                <span data-testid="hdr-stats-warn" style={{ color: "#ff9e6a" }}>
                  Stats refresh failed — showing last known data
                </span>
              )}
              {statsUi.refreshError && !stats && (
                <span data-testid="hdr-stats-error" style={{ color: "#ff9e6a" }}>
                  Stats unavailable: {statsUi.refreshError}
                </span>
              )}
              {statsUi.chainMismatch && (
                <span data-testid="hdr-chain-err" style={{ color: "#ff6a6a" }}>
                  Chain mismatch: got {statsUi.chainMismatch.got}, expected {statsUi.chainMismatch.expected}
                </span>
              )}
              {stats && (
                <>
                  <span data-testid="hdr-slots">
                    {stats.total_allocated_slots.toLocaleString()} / 1,000,000 slots
                  </span>
                  <span data-testid="hdr-parcels">{stats.parcel_count.toLocaleString()} parcels</span>
                  <span data-testid="hdr-indexed">indexed block {stats.indexed_through_block ?? "—"}</span>
                  {stats.safe_block != null && (
                    <span data-testid="hdr-safe">safe {stats.safe_block}</span>
                  )}
                  {stats.finalized_block != null && (
                    <span data-testid="hdr-finalized">finalized {stats.finalized_block}</span>
                  )}
                  {statsUi.lastGood && (
                    <span
                      data-testid="hdr-status"
                      style={{ color: degraded ? "#ff9e6a" : "#8a8a94" }}
                    >
                      {statsUi.lastGood.data_status} · reconciled={String(statsUi.lastGood.reconciled)}
                    </span>
                  )}
                </>
              )}
            </div>
            <button
              data-testid="mode-toggle"
              data-mode={mode}
              style={styles.btn(isMobile)}
              onClick={handleToggleMode}
            >
              {mode === "select" ? "Selecting…" : "Select"}
            </button>
            <button data-testid="fit-grid-btn" style={styles.btn(isMobile)} onClick={onFitGrid}>
              Fit Grid
            </button>
          </header>
        </>
      )}

      {/* WO-VRD-8 §STATUS-BADGE (Claudio) — these in-flow status banners
          caused a snervating layout shift: as stats polled, a banner
          appeared/disappeared, the canvas resized, the fit recomputed and
          the Grid jumped up/down. In the embedded page (chrome="external")
          they are suppressed entirely; the same state is surfaced by a
          single compact, ABSOLUTELY-positioned badge next to the
          "Territory" header (see below) which never reflows the canvas. */}
      {chrome !== "external" && (
        <>
          {statsUi.chainMismatch && (
            <div data-testid="chain-error-banner" style={styles.chainError}>
              Chain mismatch: got {statsUi.chainMismatch.got}, expected {statsUi.chainMismatch.expected} — viewer data suppressed.
            </div>
          )}
          {statsUi.refreshError && stats && !statsUi.chainMismatch && (
            <div data-testid="stats-refresh-warn-banner" style={styles.warnBanner}>
              Stats refresh failed — showing last known data.
            </div>
          )}
          {statsUi.refreshError && !stats && !statsUi.chainMismatch && (
            <div data-testid="stats-error-banner" style={styles.warnBanner}>
              Indexer stats unavailable — showing last known snapshot.
            </div>
          )}
          {mapStatus.kind !== "ready" && (
            <div
              data-testid={`map-status-${mapStatus.kind}`}
              style={
                mapStatus.kind.startsWith("error")
                  ? styles.warnBanner
                  : styles.rangeBanner
              }
            >
              {mapStatus.message}
            </div>
          )}
          {degraded && !statsUi.chainMismatch && (
            <div data-testid="degraded-banner" style={styles.warnBanner}>
              Data synchronization degraded — showing last known snapshot.
            </div>
          )}
          {range && !range.uniform && (
            <div data-testid="skew-banner" style={styles.rangeBanner}>
              Indexed data spans blocks {range.bmin}–{range.bmax}
            </div>
          )}
        </>
      )}
      {/* MACRO #31 PAGE 3 (Claudio) — the in-canvas mobile LIVE/CACHED/CHAIN
          badge was removed: the status now lives ONLY in the bottom Territory
          toolbar (StatusBadge), avoiding a duplicate on mobile. Desktop never
          rendered this badge. */}

      <main style={styles.main(isMobile)}>
        <div ref={wrapRef} style={styles.canvasWrap}>
          <canvas
            ref={canvasRef}
            data-testid="grid-canvas"
            data-mode={mode}
            data-fit-ready="false"
            style={{
              ...styles.canvas,
              cursor: mode === "select" ? "crosshair" : "grab",
            }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={finishPointer}
            onPointerCancel={handlePointerCancel}
            onPointerLeave={(e) => {
              if (outsideScrollRef.current) outsideScrollRef.current = null;
              if (gestureRef.current.mode !== "idle") finishPointer(e);
              setHoverPos(null);
              setHoverWorld(null);
            }}
          />
          <GridIntermediateLayer camera={interCam} items={intermediateItems} />
          <ContentPointerContext.Provider value={contentPointerArbiter}>
            <GridPublicationLayer camera={contentCam} items={pubItems} />
          </ContentPointerContext.Provider>
          <Grid3DFootprintLayer camera={contentCam} items={voxel3dItems} />
          <GridArtworkLayer camera={contentCam} items={artworkItems} />
          <Enter3DAffordance
            camera={interCam}
            items={enter3dItems}
            onEnter={(it) => {
              const found = pubItems.find((p) => p.renderable.identityId === it.identityId);
              const scene = found?.snapshot.scene_3d;
              if (scene && scene.work_arts.length > 0) {
                // Anchor the 3D scene over the territory's on-screen rectangle
                // (same math as the Enter-3D pill) so it sits ON the parcel, not
                // centred in the viewport. Camera is static while the overlay is
                // open, so the rect computed here stays correct.
                let anchorRect: { x: number; y: number; w: number; h: number } | undefined;
                const wb = interCam ? unionBoundsWorld(it.units) : null;
                if (interCam && wb) {
                  const tl = worldToScreen(interCam, wb.x, wb.y);
                  anchorRect = { x: tl.x, y: tl.y, w: wb.w * interCam.scale, h: wb.h * interCam.scale };
                }
                setEntered3d({
                  identityId: it.identityId,
                  displayName: it.displayName,
                  workArts: scene.work_arts,
                  voxelCount: it.voxelCount,
                  anchorRect,
                  artSnapshot: found?.snapshot ?? null,
                  artFootprint: wb ? { x: wb.x, y: wb.y, w: wb.w, h: wb.h } : null,
                });
              }
            }}
          />
          {entered3d && <Public3DOverlay target={entered3d} variant="grid" onExit={() => setEntered3d(null)} />}
          {hoverPos && hoveredId != null && isFinePointer && (
            <HoverTip
              parcel={allParcels.find((p) => p.id === hoveredId) ?? null}
              x={hoverPos.x}
              y={hoverPos.y}
            />
          )}
          {draftScreen &&
            selectionOverlay?.kind === "draft" &&
            selectionOverlay.selection && (
              <SelectionDimTip
                x={draftScreen.x}
                y={draftScreen.y}
                w={selectionOverlay.selection.w}
                h={selectionOverlay.selection.h}
              />
            )}
        </div>
        {chrome === "internal" && (
          <aside style={styles.panel(isMobile)} data-testid="detail-panel">
            <PanelBody
              conflict={conflict}
              detail={detail}
              cellUi={cellUi}
              statsUi={statsUi}
              hasAnySnapshot={Object.values(tiles).some((t) => t.hasSnapshot)}
            />
          </aside>
        )}
      </main>
    </div>
  );
});

// ---- Sub-components ----
function SelectionDimTip({
  x,
  y,
  w,
  h,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
}) {
  return (
    <div
      data-testid="selection-dim-tip"
      style={{
        position: "absolute",
        pointerEvents: "none",
        left: x + 14,
        top: y + 14,
        zIndex: 50,
        background: "rgba(6,14,20,0.9)",
        border: "1px solid rgba(45, 182, 240,0.55)",
        borderRadius: 5,
        padding: "1px 5px",
        font: "600 10px/1.4 ui-monospace, SFMono-Regular, Menlo, monospace",
        letterSpacing: "0.06em",
        color: "#37e0c8",
        whiteSpace: "nowrap",
        boxShadow: "0 0 10px -3px rgba(45, 182, 240,0.6)",
      }}
    >
      {w}×{h}
    </div>
  );
}

function HoverTip({ parcel, x, y }: { parcel: Parcel | null; x: number; y: number }) {
  const nameState = useTerritoryName(
    parcel ? { id: parcel.id, owner: parcel.owner } : null,
  );
  if (!parcel) return null;
  const shortOwner = parcel.owner.length > 12
    ? `${parcel.owner.slice(0, 6)}…${parcel.owner.slice(-4)}`
    : parcel.owner;
  const hasName = nameState.status === "ready";
  const identityLabel = hasName ? nameState.name : shortOwner;
  return (
    <div
      data-testid="hover-tooltip"
      style={{
        position: "absolute",
        pointerEvents: "none",
        left: x + 12,
        top: y + 12,
        background: "rgba(11,11,15,0.92)",
        border: "1px solid #22222c",
        borderRadius: 6,
        padding: "4px 8px",
        fontSize: 11,
        color: "#f5f5f7",
        whiteSpace: "nowrap",
      }}
    >
      #{parcel.id} · x={parcel.x},y={parcel.y} ·{" "}
      <span
        data-testid="hover-tooltip-identity"
        style={hasName ? { color: "#ff2d55", textShadow: "0 0 8px rgba(255,45,85,0.7)", fontWeight: 600 } : undefined}
      >
        {identityLabel}
      </span>
    </div>
  );
}

function PanelBody({
  conflict,
  detail,
  cellUi,
  statsUi,
  hasAnySnapshot,
}: {
  conflict: Parcel[] | null;
  detail: DetailData | null;
  cellUi: CellUi;
  statsUi: StatsUiState;
  hasAnySnapshot: boolean;
}) {
  if (conflict) {
    return (
      <div data-testid="conflict-warning" style={{ color: "#ff9e6a" }}>
        Data invariant conflict at this cell: {conflict.length} parcels overlap. Selection suppressed.
      </div>
    );
  }
  if (detail) {
    const p = detail.parcel;
    return (
      <div data-testid="detail-content">
        <div style={styles.parcelId}>Parcel #{p.id}</div>
        <div style={styles.k}>Owner (indexed)</div>
        <div data-testid="detail-owner" style={styles.mono}>{p.owner}</div>
        <div style={styles.k}>Position</div>
        <div>x={p.x}, y={p.y}</div>
        <div style={styles.k}>Size</div>
        <div>{p.w} × {p.h} ({p.w * p.h} slots)</div>
        {p.claim_block != null && (
          <>
            <div style={styles.k}>Claim block</div>
            <div>{p.claim_block}</div>
          </>
        )}
        {p.claim_tx && (
          <>
            <div style={styles.k}>Claim tx</div>
            <div style={styles.mono}>{p.claim_tx}</div>
          </>
        )}
        <div style={styles.k}>Snapshot</div>
        <div>as_of_block {detail.asOfBlock ?? "?"} · reconciled {String(detail.reconciled)}</div>
        {detail.fetchError && (
          <div data-testid="detail-error" style={{ color: "#ff9e6a", marginTop: 8 }}>
            Detail fetch failed: {detail.fetchError}
          </div>
        )}
      </div>
    );
  }
  if (cellUi) {
    if (cellUi.kind === "outside") {
      return (
        <div data-testid="cell-outside" style={{ color: "#8a8a94" }}>
          Outside grid ({cellUi.x}, {cellUi.y})
        </div>
      );
    }
    if (cellUi.kind === "loading-map") {
      return <div data-testid="cell-loading-map" style={{ color: "#8a8a94" }}>Loading map data…</div>;
    }
    if (cellUi.kind === "confirming") {
      return <div data-testid="cell-confirming" style={{ color: "#8a8a94" }}>Confirming cell ({cellUi.x}, {cellUi.y})…</div>;
    }
    if (cellUi.kind === "empty") {
      return (
        <div data-testid="empty-cell" style={{ color: "#8a8a94" }}>
          Cell ({cellUi.x}, {cellUi.y}): No parcel indexed at this cell (as of block {cellUi.asOf ?? "?"})
        </div>
      );
    }
    return (
      <div data-testid="cell-confirm-error" style={{ color: "#ff9e6a" }}>
        Could not confirm current cell state.
      </div>
    );
  }
  if (statsUi.chainMismatch) {
    return (
      <div data-testid="unavailable" style={{ color: "#ff6a6a" }}>
        Viewer unavailable due to chain mismatch.
      </div>
    );
  }
  if (!hasAnySnapshot && statsUi.refreshError) {
    return (
      <div data-testid="unavailable" style={{ color: "#ff9e6a" }}>
        Indexer not ready — no snapshot yet.
      </div>
    );
  }
  return <div style={{ color: "#5a5a64", fontSize: 13 }}>Click a parcel to view details.</div>;
}

const styles = {
  root: (mobile: boolean, layout: "viewport" | "fill" = "viewport"): React.CSSProperties => ({
    display: "flex",
    flexDirection: "column",
    height: layout === "fill" ? "100%" : "100vh",
    minHeight: 0,
    background: layout === "fill" ? "transparent" : "#08080b",
    color: "#f5f5f7",
    fontFamily: "'JetBrains Mono', ui-monospace, monospace",
    paddingBottom: mobile ? "env(safe-area-inset-bottom, 0)" : 0,
  }),
  header: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    padding: "12px 20px",
    borderBottom: "1px solid #1a1a22",
    background: "#0b0b0f",
  } as React.CSSProperties,
  title: {
    fontSize: 15,
    letterSpacing: "0.15em",
    fontWeight: 600,
  } as React.CSSProperties,
  stats: {
    display: "flex",
    flexWrap: "wrap",
    gap: 14,
    fontSize: 12,
    color: "#8a8a94",
    flex: 1,
  } as React.CSSProperties,
  btn: (mobile: boolean): React.CSSProperties => ({
    padding: mobile ? "10px 18px" : "6px 12px",
    background: "#f5f5f7",
    color: "#0b0b0f",
    border: 0,
    borderRadius: 6,
    fontSize: mobile ? 13 : 12,
    cursor: "pointer",
    fontFamily: "inherit",
    minHeight: mobile ? 44 : "auto",
  }),
  main: (mobile: boolean): React.CSSProperties => ({
    display: "flex",
    flexDirection: mobile ? "column" : "row",
    flex: 1,
    minHeight: 0,
  }),
  canvasWrap: {
    flex: 1,
    position: "relative",
    overflow: "hidden",
  } as React.CSSProperties,
  canvas: {
    width: "100%",
    height: "100%",
    display: "block",
    cursor: "grab",
    touchAction: "pan-y",
  } as React.CSSProperties,
  panel: (mobile: boolean): React.CSSProperties => ({
    width: mobile ? "auto" : 320,
    maxHeight: mobile ? "40vh" : "unset",
    borderLeft: mobile ? "none" : "1px solid #1a1a22",
    borderTop: mobile ? "1px solid #1a1a22" : "none",
    padding: "16px 18px",
    overflowY: "auto",
    background: "#0b0b0f",
    paddingBottom: mobile ? "calc(16px + env(safe-area-inset-bottom, 0px))" : 18,
  }),
  parcelId: { fontSize: 16, fontWeight: 600, marginBottom: 10 } as React.CSSProperties,
  k: {
    fontSize: 10,
    color: "#5a5a64",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    marginTop: 10,
  } as React.CSSProperties,
  mono: { fontSize: 12, wordBreak: "break-all" } as React.CSSProperties,
  chainError: {
    padding: "10px 20px",
    background: "#3d1010",
    color: "#ff9e6a",
    fontSize: 13,
  } as React.CSSProperties,
  warnBanner: {
    padding: "8px 20px",
    background: "#251510",
    color: "#ff9e6a",
    fontSize: 12,
  } as React.CSSProperties,
  rangeBanner: {
    padding: "6px 20px",
    background: "#101a25",
    color: "#9ac1ff",
    fontSize: 12,
  } as React.CSSProperties,
};
